import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { type Loan, lendingPaymentsCreateLoanPaymentMutation } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Button, Input, MoneyInput, formatAmount } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { useLoan } from '~/components/fields/LoanDropdown';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { getErrorMessage, invalidateLoanBalances } from '~/lib/api';
import {
  BankAccountSelector,
  LoanSelector,
  type SelectedCardRow,
  getInterestReceivable,
  getOutstandingPrincipal,
  loanIdRow,
} from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

// Loans are USD-only today; once a loan is selected its currency is used instead.
const DEFAULT_LOAN_CURRENCY = 'USD';

type CreateLoanPaymentForm = {
  description: string;
  loanId: string;
  bankAccountId: string;
  principalAmount?: number;
  amount?: number;
};

interface Params {
  id?: string;
}

const isPositive = (value?: number): value is number => typeof value === 'number' && value > 0;

const getOutstandingBalance = (loan: Loan) => getOutstandingPrincipal(loan) + getInterestReceivable(loan);

const paymentRows = (loan: Loan): SelectedCardRow[] => [
  {
    label: 'Outstanding principal',
    value: formatAmount(getOutstandingPrincipal(loan), loan.currency),
    emphasized: true,
  },
  { label: 'Interest receivable', value: formatAmount(getInterestReceivable(loan), loan.currency) },
  loanIdRow(loan),
];

export const PageLoansPaymentCreate: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleSubmitErrors } = useFormErrorHandler();
  const { id } = useParams<keyof Params>() as Params;
  const canWrite = useSessionStore((state) => state.isAuthorized({ permission: 'loans', permissionLevel: 'write' }));
  const returnRoute = id ? `${ROUTE.LOANS}/edit/${id}` : `${ROUTE.LOANS}/payments`;

  const formMethods = useForm<CreateLoanPaymentForm>({
    defaultValues: {
      description: '',
      loanId: id ?? '',
      bankAccountId: '',
    },
  });

  const { handleSubmit, watch } = formMethods;
  const loanId = watch('loanId');
  const amount = watch('amount');
  const principalAmount = watch('principalAmount');
  const { data: loan } = useLoan(loanId || undefined);
  const currency = loan?.currency ?? DEFAULT_LOAN_CURRENCY;

  const createPayment = useMutation({
    ...lendingPaymentsCreateLoanPaymentMutation(),
    onSuccess: (payment, { body }) => {
      invalidateLoanBalances(queryClient, body.loan_id);
      navigate(`${ROUTE.LOANS}/edit/${body.loan_id}`);
      addSuccessNotification({ content: `${payment.description} added` });
    },
    onError: (error) => addDangerNotification({ content: getErrorMessage(error) }),
  });

  const handleCreate = (data: CreateLoanPaymentForm) =>
    createPayment.mutate({
      body: {
        description: data.description,
        loan_id: data.loanId,
        bank_account_id: data.bankAccountId,
        principal_amount: isPositive(data.principalAmount) ? data.principalAmount : undefined,
        amount: isPositive(data.amount) ? data.amount : undefined,
        currency,
      },
    });

  const cancelButton = (
    <Button onClick={() => navigate(returnRoute)} variant="secondary" size="sm" type="button">
      Cancel
    </Button>
  );
  const createButton = (
    <Button
      onClick={handleSubmit(handleCreate, handleSubmitErrors)}
      disabled={!canWrite || createPayment.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus />
      {createPayment.isPending ? 'Creating…' : 'Create Payment'}
    </Button>
  );

  return (
    <>
      <PageActions>
        {cancelButton}
        {createButton}
      </PageActions>

      {!canWrite && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleCreate, handleSubmitErrors)}>
          {RenderFields<CreateLoanPaymentForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'description',
                    label: 'Description',
                    rules: { required: true, maxLength: { value: 127, message: 'Description is too long' } },
                    children: ({ value, onChange, onBlur, ref }, { error }) => (
                      <Input
                        ref={ref}
                        placeholder="Description"
                        aria-invalid={!!error}
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    ),
                  },
                  {
                    id: 'loanId',
                    label: 'Loan',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <LoanSelector
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        locked={Boolean(id)}
                        hasError={!!error}
                        getRows={paymentRows}
                      />
                    ),
                  },
                  {
                    id: 'bankAccountId',
                    label: 'Bank Account',
                    description: 'The account the payment is drawn from.',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <BankAccountSelector
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        hasError={!!error}
                        amount={isPositive(amount) ? amount : principalAmount}
                        amountCurrency={currency}
                        isSource
                        insufficientMessage={() => 'Insufficient balance to make this payment'}
                      />
                    ),
                  },
                  {
                    id: 'amount',
                    label: 'Amount',
                    description: 'Total paid toward interest and principal. Interest is paid first.',
                    rules: {
                      deps: ['principalAmount'],
                      validate: (value, form) => {
                        const v = value as number | undefined;
                        if (!isPositive(v) && !isPositive(form.principalAmount)) {
                          return 'Enter an Amount or a Principal Amount';
                        }
                        // The API rejects payments above the outstanding balances unless the loan allows overpayment.
                        if (loan && !loan.allow_overpayment && isPositive(v) && v > getOutstandingBalance(loan)) {
                          return `Amount exceeds the ${formatAmount(getOutstandingBalance(loan), loan.currency)} owed on this loan`;
                        }
                        return true;
                      },
                    },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <MoneyInput
                        currency={currency}
                        value={typeof value === 'number' ? value : undefined}
                        onValueChange={onChange}
                        onBlur={onBlur}
                        aria-invalid={!!error}
                      />
                    ),
                  },
                  {
                    id: 'principalAmount',
                    label: 'Principal Amount',
                    description:
                      'Portion of the amount applied to principal. Without an amount, the payment goes entirely to principal.',
                    rules: {
                      deps: ['amount'],
                      validate: (value, form) => {
                        const v = value as number | undefined;
                        if (!isPositive(v)) {
                          return true;
                        }
                        if (isPositive(form.amount) && v > form.amount) {
                          return 'Principal Amount cannot exceed the Amount';
                        }
                        if (loan && !loan.allow_overpayment && v > getOutstandingPrincipal(loan)) {
                          return `Principal Amount exceeds the ${formatAmount(getOutstandingPrincipal(loan), loan.currency)} principal outstanding on this loan`;
                        }
                        return true;
                      },
                    },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <MoneyInput
                        currency={currency}
                        value={typeof value === 'number' ? value : undefined}
                        onValueChange={onChange}
                        onBlur={onBlur}
                        aria-invalid={!!error}
                      />
                    ),
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <div className="flex items-start justify-between px-(--page-margin) py-6">
        {cancelButton}
        {createButton}
      </div>
    </>
  );
};
